import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Text from "@amzn/meridian/text";
import { Icon } from "@amzn/meridian";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-small";
import apiClient from "src/utils/apiClient";
import "./reportIssue.scss";

type ReportType = "bug" | "feature_request" | "feedback";

const REPORT_TYPES: { id: ReportType; label: string }[] = [
  { id: "bug", label: "Bug" },
  { id: "feature_request", label: "Feature request" },
  { id: "feedback", label: "Feedback" },
];

const MAX_DESCRIPTION = 1000;
const MAX_SCREENSHOTS = 3;

const CheckIcon = () => (
  <svg
    className="report-pill-check"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const CameraIcon = () => (
  <svg
    className="report-camera-icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/**
 * Report an Issue form.
 *
 * Captures a report type, a free-text description, and up to 3 optional
 * screenshots. On submit, the backend creates a SIM ticket in the
 * shared intake queue.
 */
const ReportIssuePage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reportType, setReportType] = useState<ReportType>("bug");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [screenshotUrls, setScreenshotUrls] = useState<string[]>([]);
  useEffect(() => {
    const urls = screenshots.map((file) => URL.createObjectURL(file));
    setScreenshotUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [screenshots]);

  const canSubmit = description.trim().length > 0 && !submitting;

  const handleAddScreenshots = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_SCREENSHOTS - screenshots.length;
    const next = Array.from(files).slice(0, remaining);
    setScreenshots((prev) => [...prev, ...next]);
  };

  const handleRemoveScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const form = new FormData();
      form.append("report_type", reportType);
      form.append("description", description);
      screenshots.forEach((file) => form.append("screenshots", file));

      await apiClient.post("/help/report", form);
      navigate("/help");
    } catch (error: any) {
      // The backend returns 400 with a specific reason for invalid input
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 400 && typeof detail === "string" && detail) {
        setSubmitError(detail);
      } else {
        setSubmitError("Failed to submit report. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-gradient-bg pwa-scrollable report-page">
      {/* Header */}
      <div className="report-header">
        <button
          className="report-close-button"
          onClick={() => navigate("/help")}
          aria-label="Close"
        >
          <Icon tokens={closeTokens} />
        </button>
        <Text type="h100" className="report-title">
          REPORT AN ISSUE
        </Text>
      </div>

      {/* Report type */}
      <div className="report-section-label">WHAT KIND OF REPORT IS THIS?</div>
      <div className="report-type-row">
        {REPORT_TYPES.map((t) => {
          const selected = reportType === t.id;
          return (
            <button
              key={t.id}
              className={
                "report-type-pill" +
                (selected ? " report-type-pill--selected" : "")
              }
              onClick={() => setReportType(t.id)}
            >
              {selected && <CheckIcon />}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Description */}
      <div className="report-section-label report-section-label--gap">
        WHAT HAPPENED?
      </div>
      <div className="report-textarea-wrap">
        <textarea
          className="report-textarea"
          value={description}
          maxLength={MAX_DESCRIPTION}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what happened..."
        />
        <div className="report-char-count">
          {description.length} / {MAX_DESCRIPTION}
        </div>
      </div>

      {/* Screenshots */}
      <div className="report-section-label">SCREENSHOTS (OPTIONAL)</div>
      <div className="report-screenshots">
        {screenshots.map((_, i) => (
          <div key={i} className="report-screenshot-thumb">
            <img src={screenshotUrls[i]} alt={`screenshot ${i + 1}`} />
            <button
              className="report-screenshot-remove"
              onClick={() => handleRemoveScreenshot(i)}
              aria-label="Remove screenshot"
            >
              <Icon tokens={closeTokens} />
            </button>
          </div>
        ))}
        {screenshots.length < MAX_SCREENSHOTS && (
          <button
            className="report-add-screenshot"
            onClick={() => fileInputRef.current?.click()}
          >
            <CameraIcon />
            Add a screenshot &middot; up to {MAX_SCREENSHOTS}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            handleAddScreenshots(e.target.files);
            // Reset so selecting the same file again (after removing it) still fires onChange.
            e.target.value = "";
          }}
        />
      </div>

      {/* Submit */}
      <button
        className="report-submit-button"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        Submit report
      </button>
      {submitError && <div className="report-submit-error">{submitError}</div>}
      <div className="report-submit-note">
        Creates a ticket in the WHS Data-Tech intake queue. You will get a
        reference number and a status update by email.
      </div>
    </div>
  );
};

export default ReportIssuePage;
